/**
 * useUnmappedCampaignCount — 미매핑 광고 캠페인 수 폴링 훅 (사이드바 배지용)
 *
 * 폴링 활성 조건 [C-3]:
 *   호출측(AppSidebar)이 `enabled` 로 ERP 모드 + `marketing-ad` view 권한을
 *   판정해 전달한다. enabled=false 이면 **조회 요청 자체가 발생하지 않고** count=0 고정.
 *
 * 즉각성 메커니즘 (useAdminInboxCounts 패턴 축약):
 *   1) 폴링 — 기본 5분 (인박스 20초보다 보수적 — 매핑 변경은 저빈도)
 *   2) Visibility — 탭 숨김 시 폴링 중단, 복귀 시 즉시 refresh + 재개
 *   3) BroadcastChannel(AD_MAPPING_CHANNEL) — 매핑 변경 성공 시 즉시 재조회 [C-4][기능 6]
 *
 * 실패(403/500/네트워크) 시 count=0 유지 — UI 오류를 발생시키지 않는다.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { advertisingService } from '../services/advertising';
import { AD_MAPPING_CHANNEL, AdMappingRefreshMessage } from '../constants/adMappingEvents';

const DEFAULT_INTERVAL_MS = 300_000; // 5분

export function useUnmappedCampaignCount(enabled: boolean, intervalMs = DEFAULT_INTERVAL_MS) {
  const [count, setCount] = useState(0);
  const aliveRef = useRef(true);
  const intervalIdRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const data = await advertisingService.getUnmappedCount();
      if (aliveRef.current) setCount(data.count || 0);
    } catch {
      // 403/500/네트워크 등은 조용히 0 유지 (UI 오류 없음)
      if (aliveRef.current) setCount(0);
    }
  }, [enabled]);

  const startPolling = useCallback(() => {
    if (intervalIdRef.current != null) return;
    intervalIdRef.current = window.setInterval(refresh, intervalMs);
  }, [refresh, intervalMs]);

  const stopPolling = useCallback(() => {
    if (intervalIdRef.current != null) {
      window.clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;

    // 비활성(HR 모드 또는 권한 없음) — 조회 요청 자체를 하지 않고 0 고정
    if (!enabled) {
      setCount(0);
      return () => {
        aliveRef.current = false;
        stopPolling();
      };
    }

    refresh();
    startPolling();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        refresh();
        startPolling();
      } else {
        stopPolling();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    // 매핑 변경 성공 시 즉시 재조회 (같은/다른 탭)
    let bc: BroadcastChannel | null = null;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        bc = new BroadcastChannel(AD_MAPPING_CHANNEL);
        bc.onmessage = (e: MessageEvent<AdMappingRefreshMessage>) => {
          if (e?.data?.type === 'refresh') refresh();
        };
      }
    } catch {
      /* unsupported */
    }

    return () => {
      aliveRef.current = false;
      stopPolling();
      document.removeEventListener('visibilitychange', onVisibility);
      if (bc) bc.close();
    };
  }, [enabled, refresh, startPolling, stopPolling]);

  return { count, refresh };
}
